import LegalLayout from "./LegalLayout";
import { Cookie, Shield, Check, Settings, Info } from "lucide-react";

export default function CookiePolicy() {
  return (
    <LegalLayout
      title="Cookie Policy"
      subtitle="How Virality AI uses cookies, local storage, and similar technologies to power your workspace securely."
      lastUpdated="September 10, 2026"
    >
      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
        {/* Intro */}
        <div>
          <p className="text-base text-foreground font-medium">
            This Cookie Policy explains how Virality AI (&ldquo;Virality&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) uses cookies, browser local storage, session tokens, and related web technologies when you access our platform.
          </p>
          <p className="mt-2 text-xs">
            We prioritize lightweight, privacy-first engineering. We only use cookies and local storage tokens that are strictly necessary to authenticate your account, remember your creative preferences, and protect platform security.
          </p>
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            1. What Are Cookies and Local Storage?
          </h2>
          <p className="text-xs">
            A cookie is a small data file stored on your computer or mobile device by your web browser. Similarly, browser &ldquo;Local Storage&rdquo; allows websites to store key-value pairs securely on your device without sending them on every HTTP request. These technologies allow us to recognize your browser, keep you safely signed in, and remember your interface settings.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-4">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Cookie className="h-4 w-4 text-accent" /> 2. Categories of Cookies We Use
          </h2>

          <div className="space-y-3">
            {/* Category A */}
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <h3 className="font-semibold text-foreground text-sm">Essential &amp; Security Cookies (Strictly Necessary)</h3>
              </div>
              <p className="mt-1 text-xs">
                These are required for the website to function. They allow you to log in securely, navigate between your scripts, and protect against Cross-Site Request Forgery (CSRF). Without these, our platform cannot operate.
              </p>
            </div>

            {/* Category B */}
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-accent" />
                <h3 className="font-semibold text-foreground text-sm">Functionality &amp; Experience Preferences</h3>
              </div>
              <p className="mt-1 text-xs">
                These enable the website to provide enhanced personalization. For example, remembering your dark/light color scheme mode, whether you completed the interactive product walkthrough, and your preferred editor settings.
              </p>
            </div>

            {/* Category C */}
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500" />
                <h3 className="font-semibold text-foreground text-sm">Third-Party &amp; Authentication Providers</h3>
              </div>
              <p className="mt-1 text-xs">
                When you connect your YouTube or Google account via OAuth, or purchase a subscription through Stripe / LemonSqueezy, these third parties set secure cookies to verify your identity and prevent payment fraud.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Summary Table */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            3. Common Storage Keys Used on Virality
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-elevated/70 text-foreground font-semibold border-b border-border">
                <tr>
                  <th className="p-3">Key Name</th>
                  <th className="p-3">Purpose</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Lifespan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-3 font-mono font-medium text-accent">virality_access_token</td>
                  <td className="p-3">Encrypted JWT authentication token for API access</td>
                  <td className="p-3">Local Storage / Header</td>
                  <td className="p-3">Session / 1 Hour</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono font-medium text-accent">virality_refresh_token</td>
                  <td className="p-3">Secure refresh credential to maintain your active login</td>
                  <td className="p-3">Local Storage</td>
                  <td className="p-3">30 Days</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono font-medium text-accent">virality_tour_completed</td>
                  <td className="p-3">Records that you finished or closed the product tour</td>
                  <td className="p-3">Local Storage</td>
                  <td className="p-3">Persistent</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono font-medium text-accent">theme</td>
                  <td className="p-3">Stores your interface color mode preference (light/dark)</td>
                  <td className="p-3">Local Storage</td>
                  <td className="p-3">Persistent</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Settings className="h-4 w-4 text-accent" /> 4. How Can You Control Cookies?
          </h2>
          <p className="text-xs">
            Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies or alert you when a cookie is being set.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><strong>Google Chrome:</strong> Settings &gt; Privacy and Security &gt; Third-party cookies.</li>
            <li><strong>Apple Safari:</strong> Preferences &gt; Privacy &gt; Prevent cross-site tracking.</li>
            <li><strong>Mozilla Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies and Site Data.</li>
          </ul>
          <div className="rounded-xl border border-border bg-surface p-3 text-xs flex items-start gap-2.5 mt-2">
            <Info className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <p>
              Please note that disabling strictly necessary local storage or authentication tokens will prevent you from signing in to your workspace or saving scripts.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            5. Contact Us
          </h2>
          <p className="text-xs">
            If you have questions about how we use cookies or local storage technologies, please contact our team at <a href="mailto:getintouch@viralityai.media" className="text-accent underline font-medium">getintouch@viralityai.media</a>.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}
